import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import learnerProfileQueryByExternalId from 'bundles/account-profile/components/recruiting-profile-viewer/queries/LearnerProfileByExternalId.graphql';
import learnerProfileQueryByProfileSlug from 'bundles/account-profile/components/recruiting-profile-viewer/queries/LearnerProfileByProfileSlug.graphql';
import learnerProfileQueryMe from 'bundles/account-profile/components/recruiting-profile-viewer/queries/LearnerProfileMe.graphql';
import type {
  LearnerProfileByExternalIdQuery as LearnerProfileByExternalIdQueryData,
  LearnerProfileByExternalIdQueryVariables,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileByExternalId';
import type {
  LearnerProfileByProfileSlugQuery,
  LearnerProfileByProfileSlugQueryVariables,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileByProfileSlug';
import type {
  LearnerProfileMeQuery,
  LearnerProfileMeQueryVariables,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileMe';
import { isSSR } from 'bundles/ondemand/utils/isSSR';

export const useLearnerProfileExternalIdQuery = (externalId: string) => {
  const { data, loading, refetch, error } = useQuery<
    LearnerProfileByExternalIdQueryData,
    LearnerProfileByExternalIdQueryVariables
  >(learnerProfileQueryByExternalId, {
    variables: {
      externalId,
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all', // Needed so we can still render the page if the query fails with a non critical error. E.G. Coursera History error
    skip: !externalId,
    onError: (errorObj) => {
      if (!isSSR()) {
        Sentry.captureException(errorObj, {
          extra: { message: 'Error calling the learner profile external id query.' },
        });
      }
    },
  });

  return {
    learnerProfile: data?.LearnerProfile?.queryByExternalId,
    isProfileLoading: loading,
    refetchLearnerProfile: refetch,
    error,
  };
};

export const useLearnerProfileProfileSlugQuery = (profileSlug: string) => {
  const { data, loading, refetch, error } = useQuery<
    LearnerProfileByProfileSlugQuery,
    LearnerProfileByProfileSlugQueryVariables
  >(learnerProfileQueryByProfileSlug, {
    variables: {
      profileSlug,
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all', // Needed so we can still render the page if the query fails with a non critical error. E.G. Coursera History error
    skip: !profileSlug,
    onError: (errorObj) => {
      if (!isSSR()) {
        Sentry.captureException(errorObj, {
          extra: { message: 'Error calling LearnerProfile.queryByProfileSlug' },
        });
      }
    },
  });

  return {
    learnerProfile: data?.LearnerProfile?.queryByProfileSlug,
    isProfileLoading: loading,
    refetchLearnerProfile: refetch,
    error,
  };
};

export const useLearnerProfileMeQuery = () => {
  const { data, loading, refetch, error } = useQuery<LearnerProfileMeQuery, LearnerProfileMeQueryVariables>(
    learnerProfileQueryMe,
    {
      context: {
        clientName: 'gatewayGql',
      },
      errorPolicy: 'all', // Needed so we can still render the page if the query fails with a non critical error. E.G. Coursera History error
      onError: (errorObj) => {
        if (!isSSR()) {
          Sentry.captureException(errorObj, {
            extra: { message: 'Error calling the learner profile me query.' },
          });
        }
      },
    }
  );

  return {
    learnerProfile: data?.LearnerProfile?.me,
    isProfileLoading: loading,
    refetchLearnerProfile: refetch,
    error,
  };
};
